import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Settings, ArrowLeft, Plus, X } from 'lucide-react';
import { IconCalendar } from '@tabler/icons-react';
import { useSwipe } from '@/hooks/useSwipe';
import { showIntercomWithMessage } from '@/services/integrations/intercom';
import { subscribeToStaffUnreadCount, subscribeToCustomerUnreadCount } from '@/services/data/chat';
import { getOpenTicketCount, hasUnseenTicketActivity, subscribeToTickets } from '@/services/data/tickets';
import { useTicketNotificationStore } from '@/stores/ticketNotificationStore';
import { getPendingProposalCount, subscribeToProposals } from '@/services/data/aiProposals';
import { useServiceAccess } from '@/hooks/useServiceAccess';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';
import { useKnowledgeBaseStore } from '@/stores/knowledgeBaseStore';
import clapIcon from '@/assets/ico-clap.svg';
import clapIconBlue from '@/assets/ico-clap-blue.svg';
import profileIcon from '@/assets/profile.svg';
import profileIconBlue from '@/assets/profile-blue.svg';
import calendarIcon from '@/assets/ico-calendar.svg';
import calendarIconBlue from '@/assets/ico-calendar-blue.svg';
import { SchedulingScreen } from './pages/SchedulingScreen';
import { CalendarScreen } from './pages/CalendarScreen';
import { UserInfoScreen } from './pages/UserInfoScreen';
import { BookingSuccessScreen } from './pages/BookingSuccessScreen';
import UserResourcesScreen from './pages/UserResourcesScreen';
import { signOut as signOutUser } from '@/services/data/auth';
import { useAuthStore } from './stores/authStore';
import { getBookings } from '@/services/data/bookings';
import { getVideosWithPendingResources } from '@/services/data/userAssets';
import { DesktopSidebar } from '@/components/DesktopSidebar';
import { TaskSidebar } from '@/components/TaskSidebar';
import { SchedulingSidePanel } from '@/components/SchedulingSidePanel';
import { useIsMobile } from '@/hooks/useMobile';
import { LoaderOne } from '@/components/ui/loader';
import { useEffectiveUserId } from '@/hooks/useEffectiveUserId';
import { OnboardingFlow } from '@/components/onboarding/OnboardingFlow';
import { useMarkerAutoSync } from '@/hooks/useMarkerAutoSync';
import { App as CapacitorApp } from '@capacitor/app';
import { isNativePlatform } from '@/utils/platform';

// Lazy load admin screens
const AdminCreateUserScreen = lazy(() => import('./pages/AdminCreateUserScreen'));
const AdminFakeContentScreen = lazy(() => import('./pages/AdminFakeContentScreen'));
const AdminDeleteUserScreen = lazy(() => import('./pages/AdminDeleteUserScreen'));
const AdminReschedulingSetupScreen = lazy(() => import('./pages/AdminReschedulingSetupScreen'));
const AdminPlanningSetupScreen = lazy(() => import('./pages/AdminPlanningSetupScreen'));
const AdminCrosspostSetupScreen = lazy(() => import('./pages/AdminCrosspostSetupScreen'));
const AdminNotificationsScreen = lazy(() => import('./pages/AdminNotificationsScreen'));
const AdminFeatureAccessScreen = lazy(() => import('./pages/AdminFeatureAccessScreen'));
const AdminUserViewScreen = lazy(() => import('./pages/AdminUserViewScreen'));
const AdminUserContentsScreen = lazy(() => import('./pages/AdminUserContentsScreen'));

const AdminStatsScreen = lazy(() => import('./pages/AdminStatsScreen'));
const AdminUserStatsScreen = lazy(() => import('./pages/AdminUserStatsScreen'));
const AdminContentManagerScreen = lazy(() => import('./pages/AdminContentManagerScreen'));
const AdminApiTestScreen = lazy(() => import('./pages/AdminApiTestScreen'));
const AdminUsersScreen = lazy(() => import('./pages/AdminUsersScreen'));
const AdminSyncDashboardScreen = lazy(() => import('./pages/AdminSyncDashboardScreen'));
const AdminNetworkPromptsScreen = lazy(() => import('./pages/AdminNetworkPromptsScreen'));
const AdminDocumentationScreen = lazy(() => import('./pages/AdminDocumentationScreen'));
const AdminOnboardingScreen = lazy(() => import('./pages/AdminOnboardingScreen'));
const AdminTaskBuilderScreen = lazy(() => import('./pages/AdminTaskBuilderScreen'));
const AdminDirectorDashboardScreen = lazy(() => import('./pages/AdminDirectorDashboardScreen'));
const MarkerTypeConfigScreen = lazy(() => import('./pages/MarkerTypeConfigScreen'));
const AdminStudioManagementScreen = lazy(() => import('./pages/AdminStudioManagementScreen'));
const AdminShootingContentScreen = lazy(() => import('./pages/AdminShootingContentPage'));
const AdminPostproductionDeadlinesScreen = lazy(() => import('./pages/AdminPostproductionDeadlinesScreen'));
const OpsTicketsScreen = lazy(() => import('./pages/OpsTicketsScreen'));
const CsmDashboardScreen = lazy(() => import('./pages/CsmDashboardScreen'));
const CsmTasksScreen = lazy(() => import('./pages/CsmTasksScreen'));
const MiniamakerDashboardScreen = lazy(() => import('./pages/MiniamakerDashboardScreen'));
const TechBugTrackerScreen = lazy(() => import('./pages/TechBugTrackerScreen'));
const ProductBoardScreen = lazy(() => import('./pages/ProductBoardScreen'));
const AgentLogsScreen = lazy(() => import('./pages/AgentLogsScreen'));
const QARunsDashboardScreen = lazy(() => import('./pages/QARunsDashboardScreen'));
const UserDocumentationScreen = lazy(() => import('./pages/UserDocumentationScreen'));
const UserStatsScreen = lazy(() => import('./pages/UserStatsScreen'));
const PbmTopicsScreen = lazy(() => import('./pages/PbmTopicsScreen'));
const UserTopicsScreen = lazy(() => import('./pages/UserTopicsScreen'));
const AssetsManagerScreen = lazy(() => import('./pages/AssetsManagerScreen'));
const SocialNetworksScreen = lazy(() => import('./pages/SocialNetworksScreen'));
const ShootingPreferencesScreen = lazy(() => import('./pages/ShootingPreferencesScreen'));
const DesignPreferencesScreen = lazy(() => import('./pages/DesignPreferencesScreen'));
const DescriptionPreferencesScreen = lazy(() => import('./pages/DescriptionPreferencesScreen'));
const KnowledgeBaseScreen = lazy(() => import('./pages/KnowledgeBaseScreen'));
const ProfileEditScreen = lazy(() => import('./pages/ProfileEditScreen').then(m => ({ default: m.ProfileEditScreen })));
const StaffChatScreen = lazy(() => import('./pages/StaffChatScreen').then(m => ({ default: m.StaffChatScreen })));
const ChatScreen = lazy(() => import('./pages/ChatScreen').then(m => ({ default: m.ChatScreen })));
// Admin screen configuration map for rendering
const ADMIN_SCREENS: Record<string, React.LazyExoticComponent<React.ComponentType>> = {
  'admin-create-user': AdminCreateUserScreen,
  'admin-fake-content': AdminFakeContentScreen,
  'admin-delete-user': AdminDeleteUserScreen,
  'admin-planning-setup': AdminPlanningSetupScreen,
  'admin-crosspost-setup': AdminCrosspostSetupScreen,
  'admin-rescheduling-setup': AdminReschedulingSetupScreen,
  'admin-notifications': AdminNotificationsScreen,
  'admin-feature-access': AdminFeatureAccessScreen,
  'admin-user-view': AdminUserViewScreen,
  'admin-user-contents': AdminUserContentsScreen,
  'admin-stats': AdminStatsScreen,
  'admin-content-manager': AdminContentManagerScreen,
  'admin-api-test': AdminApiTestScreen,
  'admin-users': AdminUsersScreen,
  'admin-sync-dashboard': AdminSyncDashboardScreen,
  'admin-network-prompts': AdminNetworkPromptsScreen,
  'admin-documentation': AdminDocumentationScreen,
  'admin-onboarding': AdminOnboardingScreen,
  'admin-task-builder': AdminTaskBuilderScreen,
  'director-dashboard': AdminDirectorDashboardScreen,
  'marker-type-config': MarkerTypeConfigScreen as any,
  'admin-shooting-content': AdminShootingContentScreen,
  'admin-postproduction-deadlines': AdminPostproductionDeadlinesScreen,
  'admin-studios': AdminStudioManagementScreen,
  'ops-tickets': OpsTicketsScreen,
  'csm-dashboard': CsmDashboardScreen,
  'csm-tasks': CsmTasksScreen,
  'miniamaker-dashboard': MiniamakerDashboardScreen,
  'tech-bug-tracker': TechBugTrackerScreen,
  'product-board': ProductBoardScreen,
  'agent-logs': AgentLogsScreen,
  'qa-runs': QARunsDashboardScreen,
  // Profile sub-pages with self-contained layouts
  'social-networks': SocialNetworksScreen,
  'shooting-preferences': ShootingPreferencesScreen,
  'design-preferences': DesignPreferencesScreen,
  'description-preferences': DescriptionPreferencesScreen,
  'knowledge-base': KnowledgeBaseScreen,
};

// Shared background component for mobile layouts
function MobileBackground() {
  return (
    <div className="absolute inset-0">
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-warning/10 rounded-full blur-3xl animate-slow-pulse" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-warning/10 rounded-full blur-3xl animate-slow-pulse" style={{ animationDelay: '5s' }} />
    </div>
  );
}

// Props interface for AdminScreenRenderer
interface AdminScreenRendererProps {
  currentView: string;
  adminStatsUserId: string | null;
}

// Helper component to render admin screens
function AdminScreenRenderer({
  currentView,
  adminStatsUserId
}: Readonly<AdminScreenRendererProps>) {
  // Special case for admin-stats-user which requires userId
  if (currentView === 'admin-stats-user' && adminStatsUserId) {
    return (
      <Suspense fallback={<div className="flex items-center justify-center h-64"><LoaderOne /></div>}>
        <AdminUserStatsScreen />
      </Suspense>
    );
  }

  const AdminScreen = ADMIN_SCREENS[currentView];
  if (!AdminScreen) return null;

  return (
    <Suspense fallback={<div className="flex items-center justify-center h-64"><LoaderOne /></div>}>
      <AdminScreen />
    </Suspense>
  );
}

export function MainAppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [openTicketCount, setOpenTicketCount] = useState(0);
  const [pendingProposalCount, setPendingProposalCount] = useState(0);
  const [isSchedulingDialogOpen, setIsSchedulingDialogOpen] = useState(false);
  const [showSchedulingSidePanel, setShowSchedulingSidePanel] = useState(false);
  const [showSchedulingModal, setShowSchedulingModal] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [calendarRefreshTrigger, setCalendarRefreshTrigger] = useState(0);
  const [bookingData] = useState<{
    shootingDate?: string;
    preparationDate?: string;
  }>({});
  const previousUnreadCountRef = useRef(0);

  // Extract currentView - handle nested routes like admin-stats-user/:userId or profile/social-networks
  const pathParts = location.pathname.replace('/app/', '').split('/');
  const currentView = pathParts[0] || 'scheduling';
  const subView = pathParts[1] || null;
  const adminStatsUserId = currentView === 'admin-stats-user' ? pathParts[1] : null;
  const { userEmail, isAuthenticated, isInitialized } = useAuthStore();
  const effectiveUserId = useEffectiveUserId();
  const { isComplete: isKnowledgeBaseComplete, checkKnowledgeBase } = useKnowledgeBaseStore();
  const isMobile = useIsMobile();
  const { hasAccess, isStaff } = useServiceAccess();
  useMarkerAutoSync();

  // Safety-net: reset isTransitioning when app returns to foreground
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setIsTransitioning(false);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    let removeAppStateListener: (() => void) | undefined;
    if (isNativePlatform()) {
      CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          setIsTransitioning(false);
        }
      }).then((handle) => {
        removeAppStateListener = () => handle.remove();
      });
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      removeAppStateListener?.();
    };
  }, []);

  // Initialiser le check du knowledge base quand effectiveUserId change
  useEffect(() => {
    if (effectiveUserId) {
      checkKnowledgeBase(effectiveUserId);
    }
  }, [effectiveUserId, checkKnowledgeBase]);
  const [hasBookings, setHasBookings] = useState(false);
  const [hasPendingResources, setHasPendingResources] = useState(false);

  // Check if user has any bookings and pending resources
  useEffect(() => {
    if (!effectiveUserId) return;
    
    const checkBookings = async () => {
      const result = await getBookings(effectiveUserId);
      if (result.success && result.data) {
        const now = new Date();
        const hasFutureBookings = result.data.some((b: any) => new Date(b.start_time) >= now);
        setHasBookings(hasFutureBookings);
      }
    };
    
    const checkPendingResources = async () => {
      try {
        const pending = await getVideosWithPendingResources(effectiveUserId);
        setHasPendingResources(pending.length > 0);
      } catch (error) {
        console.error('Error checking pending resources:', error);
      }
    };
    
    checkBookings();
    checkPendingResources();
  }, [effectiveUserId]);
  
  // Profile is incomplete if knowledge base is not complete OR there are pending resources
  const hasProfileIncomplete = (!isKnowledgeBaseComplete && hasBookings && hasAccess('knowledge_base')) || hasPendingResources;
  
  // Redirect to login if not authenticated (but only after initialization is complete)
  // Preserve the intended destination (including query params) in the redirect parameter
  useEffect(() => {
    if (isInitialized && !isAuthenticated) {
      const intendedDestination = location.pathname + location.search;
      const redirectParam = intendedDestination !== '/app' && intendedDestination !== '/app/' 
        ? `?redirect=${encodeURIComponent(intendedDestination)}`
        : '';
      navigate(`/login${redirectParam}`, { replace: true });
    }
  }, [isAuthenticated, isInitialized, navigate, location.pathname, location.search]);

  useEffect(() => {
    if (location.pathname === '/app' || location.pathname === '/app/') {
      const getDefaultView = () => {
        if (isStaff && isMobile) {
          return 'calendar';
        }
        if (isMobile) {
          return 'scheduling';
        }
        return 'calendar';
      };

      const defaultView = getDefaultView();
      navigate(`/app/${defaultView}`, { replace: true });
    }
  }, [location.pathname, navigate, isMobile, isStaff]);

  const handleChangeView = (newView: string) => {
    if (isTransitioning) return;

    logger.debug('🔄 Changing view from', currentView, 'to', newView);

    const SWIPEABLE_VIEWS = new Set(['scheduling', 'calendar', 'profile']);
    if (SWIPEABLE_VIEWS.has(newView) && SWIPEABLE_VIEWS.has(currentView)) {
      setIsTransitioning(true);
    }

    setShowSchedulingSidePanel(false);
    navigate(`/app/${newView}`, { replace: true });
    localStorage.setItem('currentView', newView);
  };

  const handleSwipeLeft = () => {
    logger.debug('👈 Swipe left detected, current view:', currentView);
    if (currentView === 'scheduling' && !isStaff) {
      handleChangeView('calendar');
    } else if (currentView === 'calendar') {
      handleChangeView('profile');
    }
  };

  const handleSwipeRight = () => {
    logger.debug('👉 Swipe right detected, current view:', currentView);
    if (currentView === 'profile') {
      handleChangeView('calendar');
    } else if (currentView === 'calendar' && !isStaff) {
      handleChangeView('scheduling');
    }
  };

  const swipeHandlers = useSwipe({
    onSwipeLeft: handleSwipeLeft,
    onSwipeRight: handleSwipeRight,
    minSwipeDistance: 50
  });

  const handleRequestConcierge = () => {
    const message = "Je souhaite prendre rendez-vous avec mon concierge vidéo";
    logger.debug('📨 Opening Intercom with message:', message);
    showIntercomWithMessage(message);
  };

  const handleOpenMessaging = () => {
    logger.debug('📨 Opening customer chat');
    setShowSchedulingSidePanel(false); // Close scheduling panel when opening chat
    handleChangeView('customer-chat');
  };

  const handleLogout = async () => {
    const { clearAuth } = useAuthStore.getState();
    clearAuth();
    localStorage.removeItem('currentView');
    
    const result = await signOutUser();
    if (result.success) {
      logger.debug('User logged out');
    } else {
      logger.error('Error during logout:', result.error);
    }
    navigate('/login');
  };

  const handleDeleteAccount = async () => {
    logger.debug('Delete account requested');
    
    try {
      const { deleteUserAccount } = await import('@/services/data/users');
      const result = await deleteUserAccount();
      
      if (result.success) {
        logger.debug('✅ Account deleted successfully');
        // Clear local state
        localStorage.removeItem('currentView');
        // Sign out and redirect
        await signOutUser();
        navigate('/login');
      } else {
        logger.error('❌ Failed to delete account:', result.error);
        toast.error('Erreur lors de la suppression du compte');
      }
    } catch (error) {
      logger.error('❌ Error deleting account:', error);
      toast.error('Erreur lors de la suppression du compte');
    }
  };

  // Subscribe to unread messages - staff uses staff count, customers use their own count
  const isStaffChat = hasAccess('staff_chat');
  
  useEffect(() => {
    // Use appropriate subscription based on user role
    const subscribeFunction = isStaffChat ? subscribeToStaffUnreadCount : subscribeToCustomerUnreadCount;

    const unsubscribe = subscribeFunction((count) => {
      logger.debug('📬 Chat unread count changed from', previousUnreadCountRef.current, 'to', count);
      const previous = previousUnreadCountRef.current;
      setUnreadMessages(count);
      previousUnreadCountRef.current = count;

      if (count > previous && count > 0) {
        logger.debug('🔔 New message detected');
      }
    });

    return () => { unsubscribe(); };
  }, [isStaffChat]);

  // Subscribe to open ticket count, pending proposals, and unseen activity for staff
  const { setHasUnseenActivity, getLastSeenAt } = useTicketNotificationStore();

  useEffect(() => {
    if (!isStaffChat) return;
    
    const checkUnseen = () => {
      hasUnseenTicketActivity(getLastSeenAt(), effectiveUserId || undefined).then(setHasUnseenActivity);
    };

    // Initial load
    const loadCounts = async () => {
      const [ticketCount, proposalCount] = await Promise.all([
        getOpenTicketCount(effectiveUserId || undefined),
        getPendingProposalCount()
      ]);
      setOpenTicketCount(ticketCount);
      setPendingProposalCount(proposalCount);
      checkUnseen();
    };
    loadCounts();

    // Subscribe to changes
    const unsubTickets = subscribeToTickets(() => {
      getOpenTicketCount(effectiveUserId || undefined).then(setOpenTicketCount);
      checkUnseen();
    });
    const unsubProposals = subscribeToProposals(() => {
      getPendingProposalCount().then(setPendingProposalCount);
    });

    return () => {
      unsubTickets();
      unsubProposals();
    };
  }, [isStaffChat, setHasUnseenActivity, getLastSeenAt, effectiveUserId]);

  const getViewIndex = () => {
    if (isStaff) {
      if (currentView === 'calendar') return 0;
      if (currentView === 'profile') return 1;
      return 0;
    }
    if (currentView === 'scheduling') return 0;
    if (currentView === 'calendar') return 1;
    if (currentView === 'profile') return 2;
    return 0;
  };

  // Views that manage their own full-page layout (no wrapper needed on mobile)
  const selfContainedViews = new Set([
    'admin-planning-setup',
    'admin-rescheduling-setup',
    'admin-crosspost-setup',
    'director-dashboard',
    'marker-type-config',
    'admin-shooting-content',
    'admin-postproduction-deadlines',
    'admin-studios',
    'social-networks',
    'shooting-preferences',
    'design-preferences',
    'description-preferences',
    'knowledge-base',
    'csm-dashboard',
    'csm-tasks',
    'miniamaker-dashboard',
  ]);

  // Check if current view is an admin view (but exclude self-contained ones)
  const isAdminView = (currentView.startsWith('admin-') || currentView === 'ops-tickets' || currentView === 'csm-dashboard' || currentView === 'csm-tasks' || currentView === 'tech-bug-tracker') && !selfContainedViews.has(currentView);

  // Check if current view is a self-contained view that manages its own layout
  const isSelfContainedView = selfContainedViews.has(currentView);
  
  // Check if current view is a user special view (non-swipeable screens)
  const isUserSpecialView = ['user-stats', 'help', 'pbm-topics', 'user-topics', 'assets'].includes(currentView);

  const isStaffUser = hasAccess('staff_chat');

  useEffect(() => {
    if (isStaff && currentView === 'scheduling') {
      navigate('/app/calendar', { replace: true });
    }
  }, [isStaff, currentView, navigate]);

  useEffect(() => {
    if (currentView === 'staff-chat' && !isStaffUser) {
      navigate('/app/customer-chat', { replace: true });
    }
    if (currentView === 'customer-chat' && isStaffUser) {
      navigate('/app/staff-chat', { replace: true });
    }
  }, [currentView, isStaffUser, navigate]);

  // Helper function to render main content based on current view
  const renderMainContent = () => {
    if (currentView === 'staff-chat') {
      return (
        <Suspense fallback={<div className="flex items-center justify-center h-64"><LoaderOne /></div>}>
          <StaffChatScreen onBack={() => handleChangeView('calendar')} />
        </Suspense>
      );
    }

    if (currentView === 'customer-chat') {
      return (
        <Suspense fallback={<div className="flex items-center justify-center h-64"><LoaderOne /></div>}>
          <ChatScreen onBack={() => handleChangeView('calendar')} />
        </Suspense>
      );
    }

    // Profile sub-views get full-screen layout without padding
    const isProfileSubView = currentView === 'profile' && subView;
    
    if (isProfileSubView) {
      return (
        <div className="h-full overflow-y-auto bg-black" data-app-main-scroll="true">
          {subView === 'social-networks' && (
            <Suspense fallback={<div className="flex items-center justify-center h-64"><LoaderOne /></div>}>
              <SocialNetworksScreen />
            </Suspense>
          )}
          {subView === 'shooting-preferences' && (
            <Suspense fallback={<div className="flex items-center justify-center h-64"><LoaderOne /></div>}>
              <ShootingPreferencesScreen />
            </Suspense>
          )}
          {subView === 'design-preferences' && (
            <Suspense fallback={<div className="flex items-center justify-center h-64"><LoaderOne /></div>}>
              <DesignPreferencesScreen />
            </Suspense>
          )}
          {subView === 'description-preferences' && (
            <Suspense fallback={<div className="flex items-center justify-center h-64"><LoaderOne /></div>}>
              <DescriptionPreferencesScreen />
            </Suspense>
          )}
          {subView === 'knowledge-base' && (
            <Suspense fallback={<div className="flex items-center justify-center h-64"><LoaderOne /></div>}>
              <KnowledgeBaseScreen />
            </Suspense>
          )}
          {subView === 'edit' && (
            <Suspense fallback={<div className="flex items-center justify-center h-64"><LoaderOne /></div>}>
              <ProfileEditScreen />
            </Suspense>
          )}
        </div>
      );
    }

    // Self-contained views manage their own full-page layout (no padding wrapper)
    if (isSelfContainedView) {
      return (
        <div className="h-full overflow-y-auto bg-black" data-app-main-scroll="true">
          <AdminScreenRenderer currentView={currentView} adminStatsUserId={adminStatsUserId} />
        </div>
      );
    }

    return (
      <div className="h-full min-h-screen overflow-y-auto p-2 md:p-10" data-app-main-scroll="true">
        {currentView === 'calendar' && (
          <CalendarScreen key={calendarRefreshTrigger} userId={effectiveUserId || undefined} />
        )}
        {currentView === 'profile' && !subView && (
          <UserInfoScreen
            userEmail={userEmail || ''}
            onLogout={handleLogout}
            onDeleteAccount={handleDeleteAccount}
          />
        )}
        {currentView === 'user-assets' && (
          <UserResourcesScreen />
        )}

        {currentView === 'user-stats' && (
          <Suspense fallback={<div className="flex items-center justify-center h-64"><LoaderOne /></div>}>
            <UserStatsScreen />
          </Suspense>
        )}

        {currentView === 'help' && (
          <Suspense fallback={<div className="flex items-center justify-center h-64"><LoaderOne /></div>}>
            <UserDocumentationScreen />
          </Suspense>
        )}

        {currentView === 'pbm-topics' && (
          <Suspense fallback={<div className="flex items-center justify-center h-64"><LoaderOne /></div>}>
            <PbmTopicsScreen />
          </Suspense>
        )}

        {currentView === 'user-topics' && (
          <Suspense fallback={<div className="flex items-center justify-center h-64"><LoaderOne /></div>}>
            <UserTopicsScreen />
          </Suspense>
        )}

        {currentView === 'assets' && (
          <Suspense fallback={<div className="flex items-center justify-center h-64"><LoaderOne /></div>}>
            <AssetsManagerScreen />
          </Suspense>
        )}

        {/* Admin screens */}
        <AdminScreenRenderer currentView={currentView} adminStatsUserId={adminStatsUserId} />

        <AnimatePresence>
          {currentView === 'booking-success' && (
            <BookingSuccessScreen
              open={currentView === 'booking-success'}
              shootingDate={bookingData.shootingDate}
              preparationDate={bookingData.preparationDate}
              onClose={() => handleChangeView('calendar')}
            />
          )}
        </AnimatePresence>
      </div>
    );
  };

  // Desktop layout
  if (!isMobile) {

    return (
      <div className="h-screen bg-black relative overflow-hidden flex w-full">
        {/* Desktop Sidebar */}
        <DesktopSidebar
          currentView={currentView}
          onChangeView={handleChangeView}
          onOpenMessaging={handleOpenMessaging}
          onLogout={handleLogout}
          unreadMessages={unreadMessages}
          openTicketCount={openTicketCount + pendingProposalCount}
          hasKnowledgeBaseWarning={!isKnowledgeBaseComplete && hasBookings && hasAccess('knowledge_base')}
          hasProfileIncomplete={hasProfileIncomplete}
          onOpenScheduling={() => setShowSchedulingSidePanel(true)}
          onExpandedChange={setSidebarExpanded}
          hideBookingTab={isStaff}
        />

        {/* Task Sidebar - only visible on calendar view when there are tasks */}
        {currentView === 'calendar' && <TaskSidebar />}

        {/* Main content area - fixed width calculation to avoid layout shift */}
        <div className="flex-1 h-full overflow-hidden flex flex-col">
          {/* Chat screens take full height without scroll container */}
          {renderMainContent()}
        </div>

        {/* Scheduling Side Panel - overlay positioned fixed */}
        <SchedulingSidePanel
          isOpen={showSchedulingSidePanel}
          onClose={() => setShowSchedulingSidePanel(false)}
          onDialogOpenChange={setIsSchedulingDialogOpen}
          onNavigateToCalendar={() => {
            setCalendarRefreshTrigger(prev => prev + 1);
            handleChangeView('calendar');
          }}
          sidebarExpanded={sidebarExpanded}
        />

        {/* Onboarding Flow - renders as portal overlay */}
        <OnboardingFlow />
      </div>
    );
  }

  // Mobile layout (existing)
  // Assets view has its own header, render directly without wrapper
  if (currentView === 'user-assets') {
    return (
      <div className="min-h-screen bg-black relative overflow-hidden">
        
        <div className="relative h-screen overflow-y-auto pb-[env(safe-area-inset-bottom)]">
          <UserResourcesScreen />
        </div>
      </div>
    );
  }

  // Self-contained views on mobile - they manage their own full layout (no wrapper needed)
  // NOTE: On desktop, these views are rendered via renderMainContent() with the sidebar
  if (isMobile && isSelfContainedView) {
    return (
       <div className="min-h-screen bg-black relative overflow-hidden overflow-x-hidden">
         <div className="relative h-screen overflow-y-auto overflow-x-hidden pb-[env(safe-area-inset-bottom)]">
          <Suspense fallback={<div className="flex items-center justify-center h-64"><LoaderOne /></div>}>
            <AdminScreenRenderer currentView={currentView} adminStatsUserId={adminStatsUserId} />
          </Suspense>
        </div>
      </div>
    );
  }

  // Admin views on mobile - show with back button
  if (isAdminView) {
    return (
      <div className="min-h-screen bg-black relative overflow-hidden">
        <MobileBackground />
        
        <div className="relative h-screen overflow-y-auto pb-[env(safe-area-inset-bottom)]">
          {/* Back button - with safe area */}
          <div className="sticky top-0 z-10 pt-[env(safe-area-inset-top)] bg-black/80 backdrop-blur-sm">
            <div className="p-4">
              <button
                onClick={() => handleChangeView('profile')}
                className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span>Retour</span>
              </button>
            </div>
          </div>
          
          {/* Admin screens */}
          <div className="p-4">
            <AdminScreenRenderer currentView={currentView} adminStatsUserId={adminStatsUserId} />
          </div>
        </div>
      </div>
    );
  }

  // Chat screens on mobile - full screen without any wrapper
  if (currentView === 'customer-chat' || currentView === 'staff-chat') {
    return (
      <div className="h-[100dvh] bg-background overflow-hidden" style={{ overscrollBehavior: 'none' }}>
        <Suspense fallback={<div className="flex items-center justify-center h-64"><LoaderOne /></div>}>
          {currentView === 'customer-chat' ? (
            <ChatScreen onBack={() => handleChangeView('calendar')} />
          ) : (
            <StaffChatScreen onBack={() => handleChangeView('calendar')} />
          )}
        </Suspense>
      </div>
    );
  }

  // Mobile profile sub-views - full screen without swipeable wrapper
  const isProfileSubView = currentView === 'profile' && subView;
  if (isMobile && isProfileSubView) {
    return (
      <div className="min-h-screen bg-black relative overflow-hidden">
        <div className="relative h-screen overflow-y-auto pb-[env(safe-area-inset-bottom)]">
          <Suspense fallback={<div className="flex items-center justify-center h-64"><LoaderOne /></div>}>
            {subView === 'social-networks' && <SocialNetworksScreen />}
            {subView === 'shooting-preferences' && <ShootingPreferencesScreen />}
            {subView === 'design-preferences' && <DesignPreferencesScreen />}
            {subView === 'description-preferences' && <DescriptionPreferencesScreen />}
            {subView === 'knowledge-base' && <KnowledgeBaseScreen />}
            {subView === 'edit' && <ProfileEditScreen />}
          </Suspense>
        </div>
      </div>
    );
  }

  // User special views on mobile (user-stats, help) - show with back button
  if (isUserSpecialView) {
    return (
      <div className="min-h-full bg-black p-6">
        {/* Header with back button - aligned with profile sub-pages */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => handleChangeView('profile')}
            className="w-10 h-10 flex items-center justify-center rounded-md text-white/60 hover:text-white hover:bg-white/10 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-white">
              {currentView === 'user-stats' && 'Mes statistiques'}
              {currentView === 'help' && 'Aide'}
              {currentView === 'pbm-topics' && 'Sujets'}
              {currentView === 'user-topics' && 'Mes sujets'}
              {currentView === 'assets' && 'Assets'}
            </h1>
            {currentView === 'user-stats' && (
              <p className="text-sm text-white/60">Consultez vos performances</p>
            )}
          </div>
        </div>
          
        {/* User special screens */}
        <div className="max-w-2xl mx-auto pb-24">
          {currentView === 'user-stats' && (
            <Suspense fallback={<div className="flex items-center justify-center h-64"><LoaderOne /></div>}>
              <UserStatsScreen />
            </Suspense>
          )}
          {currentView === 'help' && (
            <Suspense fallback={<div className="flex items-center justify-center h-64"><LoaderOne /></div>}>
              <UserDocumentationScreen />
            </Suspense>
          )}
          {currentView === 'pbm-topics' && (
            <Suspense fallback={<div className="flex items-center justify-center h-64"><LoaderOne /></div>}>
              <PbmTopicsScreen />
            </Suspense>
          )}
          {currentView === 'user-topics' && (
            <Suspense fallback={<div className="flex items-center justify-center h-64"><LoaderOne /></div>}>
              <UserTopicsScreen />
            </Suspense>
          )}
          {currentView === 'assets' && (
            <Suspense fallback={<div className="flex items-center justify-center h-64"><LoaderOne /></div>}>
              <AssetsManagerScreen />
            </Suspense>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      {/* Floating Tournage button — opens scheduling modal */}
      {currentView === 'calendar' && hasAccess('booking_access') && (
        <button
          onClick={(e) => { e.stopPropagation(); setShowSchedulingModal(true); }}
          className="fixed top-5 right-5 z-40 flex items-center gap-1.5 rounded-full border border-white/5 px-3.5 py-2 shadow-[0_8px_32px_rgba(0,0,0,0.3)] backdrop-blur-md active:scale-95 transition-transform"
          style={{ backgroundColor: '#1A1A1A' }}
        >
          <Plus className="h-4 w-4 text-muted-foreground" strokeWidth={2.5} />
          <span className="text-xs font-semibold text-muted-foreground">Tournage</span>
        </button>
      )}

      {/* Scheduling Modal */}
      <AnimatePresence>
        {showSchedulingModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm"
            onClick={() => setShowSchedulingModal(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="absolute inset-x-0 bottom-0 top-[env(safe-area-inset-top)] bg-black rounded-t-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="h-full overflow-y-auto">
                <div className="min-h-full bg-black p-6">
                  {/* Header with back button */}
                  <div className="flex items-center gap-4 mb-6">
                    <button
                      onClick={() => setShowSchedulingModal(false)}
                      className="text-white/60 hover:text-white hover:bg-white/10 h-10 w-10 flex items-center justify-center rounded-md transition-colors"
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </button>
                    <div>
                      <h1 className="text-xl font-semibold text-white">Réserver un tournage</h1>
                    </div>
                  </div>

                  <div className="max-w-2xl mx-auto pb-24">
                    <SchedulingScreen
                      onRequestConcierge={handleRequestConcierge}
                      onDialogOpenChange={setIsSchedulingDialogOpen}
                      onNavigateToCalendar={() => {
                        setShowSchedulingModal(false);
                        setCalendarRefreshTrigger(prev => prev + 1);
                      }}
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <div
        className="relative h-screen overflow-hidden pt-[env(safe-area-inset-top)]"
        {...swipeHandlers}
      >
        <motion.div
          className="flex h-full"
          animate={{
            x: `-${getViewIndex() * 100}%`
          }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 30
          }}
          onAnimationComplete={() => setIsTransitioning(false)}
        >
          {!isStaff && (
            <div className="w-full h-full flex-shrink-0 overflow-y-auto">
              <SchedulingScreen 
                onRequestConcierge={handleRequestConcierge}
                onDialogOpenChange={setIsSchedulingDialogOpen}
                onNavigateToCalendar={() => {
                  setCalendarRefreshTrigger(prev => prev + 1);
                  handleChangeView('calendar');
                }}
              />
            </div>
          )}

          <div className="w-full h-full flex-shrink-0 overflow-y-auto relative">
            <CalendarScreen key={calendarRefreshTrigger} userId={effectiveUserId || undefined} />
          </div>
          
          <div className="w-full h-full flex-shrink-0 overflow-y-auto">
            <UserInfoScreen 
              userEmail={userEmail || ''} 
              onLogout={handleLogout}
              onDeleteAccount={handleDeleteAccount}
            />
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {currentView === 'booking-success' && (
          <BookingSuccessScreen
            open={currentView === 'booking-success'}
            shootingDate={bookingData.shootingDate}
            preparationDate={bookingData.preparationDate}
            onClose={() => handleChangeView('calendar')}
          />
        )}
      </AnimatePresence>

      {!isSchedulingDialogOpen && (
        <>
          <div className="fixed left-6 z-50 flex items-center bottom-[max(3rem,calc(1.5rem+env(safe-area-inset-bottom)))]">
            <div className="relative rounded-full border border-white/5 shadow-[0_8px_32px_rgba(0,0,0,0.3)] px-2 py-1 backdrop-blur-md" style={{ backgroundColor: '#1A1A1A' }}>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleChangeView('calendar')}
                  className="relative flex flex-col items-center justify-center gap-1 transition-all duration-300 active:scale-95 cursor-pointer px-4 py-2 rounded-full"
                  style={currentView === 'calendar' ? { backgroundColor: '#3C3C3C' } : {}}
                >
                  <div className="relative">
                    <IconCalendar 
                      className="h-[18px] w-[18px]"
                      style={{ opacity: currentView === 'calendar' ? 1 : 0.7 }}
                      color={currentView === 'calendar' ? 'hsl(var(--warning))' : '#ffffff'}
                      strokeWidth={1.5}
                    />
                    {!isKnowledgeBaseComplete && hasBookings && hasAccess('knowledge_base') && (
                      <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-warning rounded-full" />
                    )}
                  </div>
                  <span className={`text-[9px] font-medium whitespace-nowrap ${currentView === 'calendar' ? 'text-warning' : 'text-white'}`}>
                    Planning
                  </span>
                </button>

                <button
                  onClick={() => handleChangeView('profile')}
                  className="relative flex flex-col items-center justify-center gap-1 transition-all duration-300 active:scale-95 cursor-pointer px-4 py-2 rounded-full"
                  style={currentView === 'profile' ? { backgroundColor: '#3C3C3C' } : {}}
                >
                  <Settings 
                    className="h-[18px] w-[18px]"
                    style={{ opacity: currentView === 'profile' ? 1 : 0.7 }}
                    stroke={currentView === 'profile' ? 'hsl(var(--warning))' : '#ffffff'}
                    strokeWidth={1.5}
                  />
                  <span className={`text-[9px] font-medium whitespace-nowrap ${currentView === 'profile' ? 'text-warning' : 'text-white'}`}>
                    Profile
                  </span>
                </button>
              </div>
            </div>
          </div>

          <div className="fixed right-6 z-50 flex items-center bottom-[max(3rem,calc(1.5rem+env(safe-area-inset-bottom)))]">
            <button
              onClick={handleOpenMessaging}
              className="relative rounded-full border border-white/5 shadow-[0_8px_32px_rgba(0,0,0,0.3)] backdrop-blur-md text-white transition-all duration-200 flex items-center justify-center h-[58px] w-[58px]"
              style={{ backgroundColor: '#1A1A1A' }}
            >
              <MessageCircle className="w-[22px] h-[22px]" />
              {unreadMessages > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold animate-scale-in">
                  {unreadMessages}
                </span>
              )}
            </button>
          </div>

          {/* Onboarding Flow - renders as portal overlay */}
          <OnboardingFlow />
        </>
      )}
    </div>
  );
}
